import { useQuery } from '@tanstack/react-query'
import { searchExperiences } from 'tripPlanner/api'
import * as ExperienceDetailKeys from 'tripPlanner/hooks/reactQueryKeys/experienceDetail'
import type { ExperienceSearchFallbackPlace } from 'tripPlanner/api/types'
import type { DrawerExperienceSearchTile } from './ExperienceDetailView'
import { mapExperienceDetailToDrawer } from './mapExperienceDetailToDrawer'

const EMPTY_EXPERIENCES: Array<DrawerExperienceSearchTile> = []
const EXPERIENCE_SEARCH_STALE_TIME = 5 * 60 * 1000

function mapSearchResultToDrawer(
  item: Awaited<ReturnType<typeof searchExperiences>>['experiences'][number],
): DrawerExperienceSearchTile {
  return mapExperienceDetailToDrawer(item)
}

interface UseExperienceSearchOptions {
  searchQuery: string
  region?: string
  fallbackPlace?: ExperienceSearchFallbackPlace
  enabled: boolean
}

export default function useExperienceSearch({
  searchQuery,
  region,
  fallbackPlace,
  enabled,
}: UseExperienceSearchOptions) {
  const queryResult = useQuery({
    queryKey: ExperienceDetailKeys.search(searchQuery, region, fallbackPlace?.placeId),
    queryFn: () => searchExperiences(searchQuery, region, fallbackPlace),
    enabled: enabled && !!searchQuery,
    select: (data) => ({
      experiences: data.experiences.map(mapSearchResultToDrawer),
      searchContext: data.searchContext,
    }),
    staleTime: EXPERIENCE_SEARCH_STALE_TIME,
  })

  return {
    experiences: queryResult.data?.experiences ?? EMPTY_EXPERIENCES,
    searchContext: queryResult.data?.searchContext,
    isLoading: queryResult.isLoading && queryResult.fetchStatus !== 'idle',
    isError: queryResult.isError,
    refetch: queryResult.refetch,
  }
}
